import { useEffect, useState } from 'react';
import { ImageItem } from '../components/Cards/Types';
import { ISortingFilters, TagItem } from '../components/Tag/types';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/system';
import { Divider, Paper, Typography } from '@mui/material';
import Theme from '../Themes/main.theme';
import CardGrid from '../components/CardGrid';
import HttpService from '../services/HttpService';
import GMapsPlaceMarker from '../components/Maps/GMapsPlaceMarker';
import { eliminateOjectNotTruthyValues } from '../lib/helperFuncs';
import UserService from '../services/UserService';
import { Permissions } from '../lib/permissions';
import { useApiState } from '../hooks/useApiState';
import { ApiUsability } from '../lib/generalTypes';
import { useSnackbar } from 'notistack';

const ITEMS_PER_PAGE = 8;

const UserRegistrations = () => {
  const [geoData, setGeoData] = useState<google.maps.LatLngLiteral>();
  const [data, setData] = useState<ImageItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<string | undefined>('1');
  const [selectedTags, setSelectedTags] = useState<TagItem[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { apiEnabled, loadingApi } = useApiState(ApiUsability.Google_Maps, true);

  document.title = t('Καταχωρίσεις');

  const sortingItems: ISortingFilters[] | undefined = [{
    id: '1',
    refValue: 'title',
    sortOrder: 'asc',
    label: t('Αλφαβητικά')
  }, {
    id: '2',
    refValue: 'locations:locationsCategory',
    sortOrder: 'asc',
    label: t('Κατηγορία'),
  },
  {
    id: '3',
    refValue: 'created',
    sortOrder: 'desc',
    label: t('Νεότερης Καταχώρισης'),
  },];

  const handleSortingChange = (sortingItemId?: string) => {
    setSortBy(sortingItemId);
  };

  const handleOnPageChange = (page: number) => {
    setSelectedPage(page);
  };

  const handleTagChange = (selectedTagIds: any) => {
    setSelectedTags([...selectedTagIds]);
  };

  const getRegistrations = async () => {    
    setIsLoading(true);
    try {
      const filters: any = {
        sort_by: sortBy,
        sort_order: 'desc',
        page: selectedPage,
        ...(UserService.hasRole([Permissions.PermissionExternalLocationReadMy])
          && {
            location: true
          }
        ),
        ...(UserService.hasRole([Permissions.PermissionExternalProfessionalReadMy])
          && {
            professional: true
          }
        )
      };
      const resp = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/registrations`, {
        params: eliminateOjectNotTruthyValues(filters)
      });

      const results: ImageItem[] = resp?.data.data.map((x: any) => {        
        return {
          id: x?.['o:id'],
          is_published: x?.['o:is_public'],
          title: x?.['o:title'],
          alwayShowBanner: true,
          img: x?.thumbnail_display_urls.large,
          displayEditAndPublish:
            (Permissions.PermissionExternalLocationApplicationModificationCreateMy && x?.type === 'location')
            || (Permissions.PermissionExternalProfessionalApplicationModificationCreateMy && x?.type === 'professional'),
          redirect: x?.type === 'location'
            ? 'location/details'
            : 'professionals/details',
          type: x?.type
        };
      });

      const mapData: google.maps.LatLngLiteral = resp?.data.data.map((x: any) => {
        return {
          id: x['o:id'],
          title: x['o:title'],
          img: x.thumbnail_display_urls.square,
          lat: Number(x?.['locations:latitude']?.[0]?.['@value']),
          lng: Number(x?.['locations:longitude']?.[0]?.['@value']),
        };
      });
      
      setGeoData(mapData);
      setData(results);
      setTotalCount(+resp.data.total_count);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const onChangeRegistration = async (
    id: any,
    is_published: boolean,
    type: string | undefined
  ) => {    
    const oldData = { ...data};
    try {
      setData(data.map(x=> {
        return {
          ...x,
          is_published: !x.is_published
        };
      }));      
      const resp = await HttpService.getAxiosClient().patch(`${process.env.REACT_APP_BACKEND_URL}/registrations/${id}`, {
        id: id,
        is_published: is_published,
        type: type
      });
      if (resp.status === 200) {
        if (is_published) {
          
          enqueueSnackbar(`${t('Επιτυχής αλλαγή κατάστασης της καταχώρισης σας')}`, { variant: 'success' });
        } else {
          enqueueSnackbar(`${t('Η καταχώριση θα αφαιρεθεί απο τη λίστα αγαπημένων και από την αναζήτηση')}`, { variant: 'info' });
          enqueueSnackbar(`${t('Μετά από 2 μήνες θα διαγραφούν τελέιως τα στοιχεία από το σύστημα')}`, { variant: 'info' });
        }
      }
    } catch (error) {
      console.error(error);
      setData(oldData);
      enqueueSnackbar(t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'), { variant: 'error' });
    }
  };

  useEffect(() => {
    getRegistrations();
  }, []);

  return (
    <>
    
      <Typography sx={{ my: 2 }} variant='h2'
        color={Theme?.labelColor?.main}>{t('Καταχωρίσεις')}</Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      <Box display='flex'
        justifyContent='space-between'>
        <Typography color={Theme?.labelColor?.main} variant='h3'
          ml={3} sx={{ alignSelf: 'center' }}>
          {totalCount} {t('Καταχωρίσεις')}
        </Typography>
      </Box>
      <CardGrid
        grid={{  
          gridData: data,
          onTagClick: handleTagChange,
          onChangeRegistration: onChangeRegistration,
          selectedTags: selectedTags,
        }}
        isLoading={isLoading}
        pagination={{
          serverSidePagination: false,
          itemsPerPage: ITEMS_PER_PAGE,
          totalResults: totalCount,
          selectedPage: selectedPage,
          handleOnPageChange: handleOnPageChange
        }}
        sorting={{
          clientSideSorting: false,
          handleSortingChange: handleSortingChange,
          defaultValue: sortBy,
          sortingItems: sortingItems
        }}
      />
      {(apiEnabled && !loadingApi)
        ? <Box mt={2}>
          <Paper variant='outlined'
            sx={{ height: 'fit-content', borderRadius: 4, boxShadow: 0, backgroundColor: 'transparent' }}>
            <Box m={2}>
              <GMapsPlaceMarker
                height='600px'
                zoom={7}
                location={geoData}
              />
            </Box>
          </Paper>
        </Box>
        : null
      }
    </>

  );
};

export default UserRegistrations;